import { createAction, createActionGroup, emptyProps, props } from '@ngrx/store';

import { FeatureDeprecated, ViError } from '@vi-iot/client';

import { PAIRING_STEPS } from '@store';

import { UpdateFeatureConfig } from '../_models/feature.model';

import {
  COMMISSIONING_STEP,
  RemoteDeviceCode,
  RemoteDeviceType,
  ValidateEuidResult,
  ZIGBEE_COORDINATOR_STATUS,
} from './commissioning.model';
import {
  CommissioningDeviceAssignChannelParams,
  CommissioningDeviceAssignHeatingCircuitParams,
  CommissioningRemoteDeviceAssignHeatingCircuitParams,
  CommissioningDeviceAssignRoomParams,
  CommissioningDeviceAssignValidationParams,
  CommissioningRemoteDeviceAssignVentilationParams,
} from './commissioning.types';

export namespace CommissioningActions {
  export enum Type {
    // API
    LIST_FEATURES = '[COMMISSIONING] List Features',
    LIST_FEATURES_SUCCESS = '[COMMISSIONING] List Features Success',
    LIST_FEATURES_ERROR = '[COMMISSIONING] List Features Error',
    GET_FEATURE = '[COMMISSIONING] Get Feature',
    GET_FEATURE_SUCCESS = '[COMMISSIONING] Get Feature Success',
    GET_FEATURE_ERROR = '[COMMISSIONING] Get Feature Error',
    UPDATE_FEATURE_PROPERTY = '[COMMISSIONING] Update Feature Property',
    UPDATE_FEATURE_PROPERTY_SUCCESS = '[COMMISSIONING] Update Feature Property Success',
    UPDATE_FEATURE_PROPERTY_ERROR = '[COMMISSIONING] Update Feature Property Error',
    UPDATE_PAIRING_STATUS = '[COMMISSIONING] Update Pairing Status',
    UPDATE_PAIRING_STATUS_SUCCESS = '[COMMISSIONING] Update Pairing Status Success',
    UPDATE_PAIRING_STATUS_ERROR = '[COMMISSIONING] Update Pairing Status Error',
    SET_CONNECTING_PAIRING_STATUS = '[COMMISSIONING] Set Connecting Pairing Status',

    // Live Updates
    LIVE_UPDATE_FEATURE = '[COMMISSIONING] Live Update Feature',

    // UI
    RESET_EUID_VALIDATION = '[COMMISSIONING] Reset EUID Validation',
    RESET_FEATURES = '[COMMISSIONING] Reset Features',
    INIT_COMMISSIONING = '[COMMISSIONING] Init Commissioning',
    SET_CODE = '[COMMISSIONING] Set Code',
    SET_ACTIVE_STEP = '[COMMISSIONING] Set Active Step',
    PAIRING_TIMEOUT = '[COMMISSIONING] Pairing Timeout',
    SET_PAIRING_STATUS = '[COMMISSIONING] Set Pairing Status',
    CHANGE_DEVICE_TYPE = '[COMMISSIONING] Change Device Type',
    RESET_UPDATE_FEATURE_PROPERTY = '[COMMISSIONING] Reset Update Feature Property',

    RESET_COMMISSIONING_STATE = '[COMMISSIONING] Reset Commissioning State',
  }

  export const verifyEuid = createActionGroup({
    source: '[COMMISSIONING] Verify EUID',
    events: {
      attempt: emptyProps(),
      success: emptyProps(),
      error: props<{ error: ValidateEuidResult }>(),
    },
  });

  export const deviceAssign = createActionGroup({
    source: '[COMMISSIONING] Device Assign',
    events: {
      room: props<{
        data: CommissioningDeviceAssignRoomParams;
      }>(),
      heatingCircuit: props<{
        data: CommissioningDeviceAssignHeatingCircuitParams;
      }>(),
      ventilation: props<{
        data: CommissioningRemoteDeviceAssignVentilationParams;
      }>(),
      remoteHeatingCircuit: props<{
        data: CommissioningRemoteDeviceAssignHeatingCircuitParams;
      }>(),
      channels: props<{
        data: CommissioningDeviceAssignChannelParams[];
      }>(),
      attempt: emptyProps(),
      attemptcomplete: emptyProps(),
      sequenceSuccess: props<{ data: any }>(),
      success: props<{
        validationData: CommissioningDeviceAssignValidationParams[];
      }>(),
      reset: emptyProps(),
      sequenceError: props<{ error?: ViError | any; data: any }>(),
      error: props<{
        validationData: CommissioningDeviceAssignValidationParams[];
      }>(),
    },
  });

  export const resetEuidValidation = createAction(Type.RESET_EUID_VALIDATION);

  export const resetFeatures = createAction(Type.RESET_FEATURES);

  export const listFeatures = createAction(Type.LIST_FEATURES);

  export const listFeaturesSuccess = createAction(
    Type.LIST_FEATURES_SUCCESS,
    props<{
      feature: { [featureName: string]: FeatureDeprecated };
      installationId: number;
      gatewaySerial: string;
    }>()
  );

  export const listFeaturesError = createAction(Type.LIST_FEATURES_ERROR);

  export const getFeature = createAction(
    Type.GET_FEATURE,
    props<{
      installationId: number;
      gatewaySerial: string;
      deviceId: string;
    }>()
  );
  export const getFeatureSuccess = createAction(
    Type.GET_FEATURE_SUCCESS,
    props<{
      feature: FeatureDeprecated;
    }>()
  );

  export const getFeatureError = createAction(Type.GET_FEATURE_ERROR);

  export const liveUpdateFeature = createAction(
    Type.LIVE_UPDATE_FEATURE,
    props<{ featureName: string; feature: FeatureDeprecated }>()
  );

  export const updateFeatureProperty = createAction(
    Type.UPDATE_FEATURE_PROPERTY,
    props<{
      featureName: string;
      value: any;
      propertyName: string | string[];
      paramName: string | string[];
      commandName?: string;
      config?: UpdateFeatureConfig;
    }>()
  );

  export const updateFeaturePropertySuccess = createAction(
    Type.UPDATE_FEATURE_PROPERTY_SUCCESS,
    props<{
      deviceId: string;
      config?: UpdateFeatureConfig;
      isAlreadyAdded: boolean;
    }>()
  );

  export const updateFeaturePropertyError = createAction(
    Type.UPDATE_FEATURE_PROPERTY_ERROR,
    props<{ featureName: string; property: string | string[]; error?: any }>()
  );

  export const resetUpdateFeatureProperty = createAction(Type.RESET_UPDATE_FEATURE_PROPERTY);

  export const setActiveStep = createAction(
    Type.SET_ACTIVE_STEP,
    props<{ step: COMMISSIONING_STEP }>()
  );
  export const initCommissioning = createAction(
    Type.INIT_COMMISSIONING,
    props<{ isSpecificRoomSelected: boolean }>()
  );

  export const pairingTimeout = createAction(Type.PAIRING_TIMEOUT);

  export const setPairingStatus = createAction(
    Type.SET_PAIRING_STATUS,
    props<{
      status: ZIGBEE_COORDINATOR_STATUS;
      step: PAIRING_STEPS;
    }>()
  );

  export const updatePairingStatus = createAction(Type.UPDATE_PAIRING_STATUS);

  export const setConnectingPairingStatus = createAction(
    Type.SET_CONNECTING_PAIRING_STATUS,
    props<{
      status: ZIGBEE_COORDINATOR_STATUS;
      step: PAIRING_STEPS;
      deviceId: string;
      installationId: number;
      gatewaySerial: string;
    }>()
  );

  export const updatePairingStatusSuccess = createAction(
    Type.UPDATE_PAIRING_STATUS_SUCCESS,
    props<{ coordinatorStatus: ZIGBEE_COORDINATOR_STATUS }>()
  );

  export const updatePairingStatusError = createAction(
    Type.UPDATE_PAIRING_STATUS_ERROR,
    props<{ step?: COMMISSIONING_STEP; status?: ZIGBEE_COORDINATOR_STATUS }>()
  );

  export const setCode = createAction(Type.SET_CODE, props<{ code: RemoteDeviceCode }>());

  export const changeDeviceType = createAction(
    Type.CHANGE_DEVICE_TYPE,
    props<{ deviceType: RemoteDeviceType }>()
  );

  export const resetCommissioningState = createAction(Type.RESET_COMMISSIONING_STATE);
}
